import { useGridApiRef } from '@mui/x-data-grid-pro';
import { POST_UNREALISED_FX_GAINS_SUCCESS } from 'adminConstants/routes';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import InteractiveTable, { Column } from 'components/InteractiveTable';
import { Row } from 'components/Layout';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import Spacing from 'components/Spacing';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  CurrencyRateDetails,
  TransactionOperation,
} from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { useDisclosure } from 'further-ui/hooks';
import { numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useEffect, useState } from 'react';
import AssetsHistory from './AssetsHistory';

const pageId = 'post-unrealised-asset-fx-gains';

const columns: Array<Column> = [
  {
    field: 'investmentName',
    headerName: 'Investment',
    renderCell: ({ row }) => row.bankName,
    flex: 1,
    sortable: false,
  },
  {
    field: 'holdingClassName',
    headerName: 'Holding class',
    flex: 1,
    sortable: false,
  },
  {
    field: 'investmentAmountTcy',
    headerName: 'Hold value (TCY)',
    renderCell: ({ row }) =>
      numberToCurrencyString(row.investmentAmountTcy, {
        fallback: '-',
        currency: row.investmentCurrency,
      }),
    flex: 1,
  },
  {
    field: 'originalValueLcy',
    headerName: 'Original value (LCY)',
    renderCell: ({ row }) =>
      numberToCurrencyString(row.calculationBreakdown?.originalValueLcy, {
        fallback: '-',
        currency: row.baseCurrency,
      }),
    flex: 1,
    sortable: false,
  },
  {
    field: 'runningTotalUnrealisedLcy',
    headerName: 'Running total unrealised',
    renderCell: ({ row }) =>
      numberToCurrencyString(
        row.calculationBreakdown?.runningTotalUnrealisedLcy,
        {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        },
      ),
    flex: 1,
    sortable: false,
  },
  {
    field: 'currentValueLcy',
    headerName: 'Current value (LCY)',
    renderCell: ({ row }) =>
      numberToCurrencyString(row.calculationBreakdown?.currentValueLcy, {
        fallback: '-',
        currency: row.baseCurrency,
      }),
    flex: 1,
    sortable: false,
  },
  {
    field: 'unrealisedAssetFxGainValueLcy',
    headerName: 'Unrealised gain/loss (LCY)',
    renderCell: ({ row }) =>
      numberToCurrencyString(row.unrealisedAssetFxGainValueLcy, {
        fallback: '-',
        currency: row.accountCurrency,
        negativeInBrackets: true,
      }),
    flex: 1,
    sortable: false,
  },
];

type Props = {
  vehicleId: string;
  currencyRateOverrides: Map<CurrencyCode, CurrencyRateDetails>;
  onCurrenciesLoaded: (
    currencies: Array<[CurrencyCode, CurrencyRateDetails]>,
  ) => void;
};

const Assets: React.FC<Props> = ({
  vehicleId,
  currencyRateOverrides,
  onCurrenciesLoaded,
}) => {
  const { vehicle } = useLayoutContext();
  const { hasEqualisationInDraftState } = useEqualisations(vehicleId);
  const confirmDialog = useDisclosure();
  const apiRef = useGridApiRef();

  const [transactionChanges, setTransactionChanges] = useState<
    Map<
      string,
      Array<{
        transactionTypeId: string;
        operationType: TransactionOperation;
      }>
    >
  >(new Map());

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const { data, isFetching } = api.unrealisedAssetFxGainOrLoss.list.useQuery({
    vehicleId,
    ...(currencyRateOverrides
      ? { currencyRateOverrides: Object.fromEntries(currencyRateOverrides) }
      : {}),
  });

  useEffect(() => {
    if (!data?.currencyRates) return;

    onCurrenciesLoaded(data.currencyRates);
  }, [data?.currencyRates, onCurrenciesLoaded]);

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: createUnrealisedAssetFXGains, isPending } =
    api.unrealisedAssetFxGainOrLoss.create.useMutation(
      mutationOpts({
        successMessage:
          'Created unrealised asset FX gains/losses successfully.',
        successRedirect: POST_UNREALISED_FX_GAINS_SUCCESS,
      }),
    );

  const rows =
    data?.results.map((row) => ({
      id: `${row.investmentId}-${row.holdingClassId}`,
      isExpandable: true,
      baseCurrency: vehicle?.baseCurrency,
      ...row,
    })) ?? [];

  const postUnrealisedFXGains = () => {
    const postUnrealisedFXGainsBody = rows.map((row) => {
      const transactions = transactionChanges.get(row.id);
      return {
        investmentId: row.investmentId,
        holdingClassId: row.holdingClassId,
        transactions: transactions ?? row.transactions,
      };
    });
    createUnrealisedAssetFXGains({
      vehicleId,
      holdingClassInvestments: postUnrealisedFXGainsBody,
      currencyRateOverrides: Object.fromEntries(currencyRateOverrides),
    });
  };

  const handleTransactionChange = (
    rowId: string,
    newTransactions: Array<{
      transactionTypeId: string;
      operationType: TransactionOperation;
    }>,
  ) => {
    setTransactionChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(rowId, newTransactions);
      return newMap;
    });

    apiRef.current.updateRows([
      {
        id: rowId,
        transactions: newTransactions,
      },
    ]);
  };

  if (!vehicle) {
    return null;
  }

  return (
    <Spacing>
      <InteractiveTable
        apiRef={apiRef}
        id={pageId}
        columns={columns}
        rows={rows}
        scrollable
        loading={isFetching}
        getExpandedContent={(row) => (
          <Row fullWidth>
            {!transactionTypesLoading && (
              <TransactionAllocator
                transactionTypeOptions={transactionTypeOptions}
                transactions={row.transactions}
                onChange={(newTransactions) => {
                  handleTransactionChange(row.id ?? '', newTransactions);
                }}
                transactionCurrency={row.currencyCode}
                vehicle={vehicle}
                fxRate={row.currencyRate}
              />
            )}
          </Row>
        )}
      />
      <ButtonGroup>
        <Button
          variant="contained"
          color="primary"
          onClick={
            hasEqualisationInDraftState
              ? confirmDialog.onOpen
              : postUnrealisedFXGains
          }
          disabled={isPending}
        >
          Confirm and proceed
        </Button>
      </ButtonGroup>
      <AssetsHistory vehicleId={vehicleId} />
      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={postUnrealisedFXGains}
          loading={isPending}
        />
      )}
    </Spacing>
  );
};

export default Assets;
